import { PlatformLocation } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  Company,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  User,
} from '@site-mate/dashpivot-shared-library';
import { CompanyPlanTypes, SubscriptionStatuses } from '@site-mate/sitemate-global-shared';

import { AuthService } from 'app/auth/auth.service';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { MenuService } from 'app/layout/menu.service';
import { SegmentService } from 'app/segment/segment.service';
import { TeamWeb } from 'app/shared/model/team.model';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { TeamService } from 'app/shared/service/team.service';
import { TimingMarkerService } from 'app/shared/service/timing-marker.service';
import { ProfileService } from 'app/user/profile.service';
import { UserService } from 'app/user/user.service';

import { CustomTitleService } from '../../shared/service/custom-title/custom-title.service';
import { NavService } from '../nav.service';

@Component({
  selector: 'cc-top-nav',
  templateUrl: 'top-nav.component.html',
  styleUrls: ['top-nav.component.scss'],
})
export class TopNavComponent implements OnInit {
  currentFolder: TeamWeb;
  loading: boolean;
  selectedApp;
  show: boolean;
  title: string;
  user: User;
  unSubscribe = new Subject<void>();
  companyHasFreePlan: boolean;
  canUpgradeSubscription: boolean;
  company: Company;
  team: TeamWeb;
  handler;
  passwordChangeLink: string;
  isSSOUser: boolean;
  disabledPasswordChangeTooltipText: string;

  constructor(
    private readonly teamService: TeamService,
    private readonly menuService: MenuService,
    private readonly segmentService: SegmentService,
    public readonly userService: UserService,
    private readonly location: PlatformLocation,
    private readonly customTitleService: CustomTitleService,
    private readonly navService: NavService,
    private readonly profileService: ProfileService,
    private readonly timingMarkerService: TimingMarkerService,
    private readonly authService: AuthService,
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly companyService: CompanyService,
    private readonly errorHandler: ErrorHandler,
    private readonly i18nService: TmpI18NService,
    private readonly reCaptchaV3Service: ReCaptchaV3Service,
  ) {}

  isProjectControllerOrAbove$ = this.userService.isProjectControllerOrAbove$();
  isCompanyControllerOrAbove$ = this.userService.isCompanyControllerOrAbove$();
  isTeamControllerOrAbove$ = this.userService.isTeamControllerOrAbove$();

  async ngOnInit() {
    this.location.onPopState(this.handler);

    this.onCompanyUpdate();
    this.onCurrentTeamUpdate();

    this.menuService.updateSelectedAppEvent.subscribe((app) => {
      this.selectedApp = app;
    });

    this.menuService.loadingEvent.subscribe(() => {
      this.loading = true;
    });

    this.menuService.toggleEvent.subscribe((show) => {
      this.show = show;
    });

    this.teamService.currentTeam.subscribe((team) => {
      this.currentFolder = team;
      this.loading = false;
    });

    this.userService.currentUser
      .pipe(
        takeUntil(this.unSubscribe),
        filter((user) => Object.keys(user).length > 0),
      )
      .subscribe((user) => {
        this.user = user;
        this.verifySSODomain(user.email);
      });

    this.passwordChangeLink = await this.getPasswordChangeLink();
    this.disabledPasswordChangeTooltipText = this.i18nService.getMessage('disabledChangePasswordForSSO');
  }

  onCurrentTeamUpdate(): void {
    this.teamService.currentTeam
      .pipe(
        takeUntil(this.unSubscribe),
        filter((team: TeamWeb) => !!team.id),
      )
      .subscribe((team: any) => {
        this.team = team;
        this.setUpCompany(team.companyMetadata);
      });
  }

  onCompanyUpdate(): void {
    this.navService.onCompanyMetadataUpdate.pipe(takeUntil(this.unSubscribe)).subscribe((company: any) => {
      this.team = { ...company, isCompany: true };
      this.setUpCompany(company);
    });
  }

  private setUpCompany(company: any): void {
    this.company = company;
    this.companyHasFreePlan = this.isCompanyInFreeTrial(company);
    this.canUpgradeSubscription = this.canCompanyBeUpgraded(company);
  }

  isCompanyInFreeTrial(company: Company) {
    return company?.billing?.planType === CompanyPlanTypes.FreeTrial;
  }

  canCompanyBeUpgraded(company: Company): boolean {
    const subscriptionStatus = company?.billing?.subscription?.status;
    return [
      SubscriptionStatuses.FreeTrial,
      SubscriptionStatuses.ExpiredTrial,
      SubscriptionStatuses.Paused,
      SubscriptionStatuses.Churned,
    ].includes(subscriptionStatus);
  }

  onItemClick(tabName: string) {
    const { isCompany, isProject } = this.currentFolder;

    // eslint-disable-next-line no-nested-ternary
    const hierarchy = isCompany ? 'Organisation' : isProject ? 'Project' : 'Team';

    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.NavigationClicked, {
        Context: tabName,
        Hierarchy: hierarchy,
      }),
      this.segmentService,
    );
    this.customTitleService.setTitle(`${tabName} - Dashpivot`);
  }

  getOrg() {
    if (this.currentFolder.isCompany) {
      return 'companies';
    }
    if (this.currentFolder.isProject) {
      return 'projects';
    }
    return 'teams';
  }

  private async getPasswordChangeLink() {
    const oidcConfig = await this.oidcSecurityService.getConfiguration().toPromise();

    const params = new URLSearchParams({
      client_id: oidcConfig.clientId,
      redirect_uri: oidcConfig.redirectUrl,
      email: this.user.email,
    });
    return `${oidcConfig.authority}/password/forgot?${params}`;
  }

  getUsersPageLink() {
    return `/users/${this.getOrg()}/${this.currentFolder.id}`;
  }

  getHomePageLink() {
    return `/home/${this.getOrg()}/${this.currentFolder.id}/activity`;
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  editProfile() {
    void this.profileService.showEditProfile();
    this.callRecaptchaV3();
  }

  logout() {
    const timingMarker = this.timingMarkerService.start(new DashpivotEvent(EventTypes.UserLoggedOut));
    void this.authService.logout();
    this.teamService.clearCurrentTeam();
    setTimeout(() => {
      this.timingMarkerService.finish(timingMarker);
    }, 200);
  }

  onChangePasswordClick(event: MouseEvent) {
    if (this.isSSOUser) {
      event.preventDefault();
    }
  }

  callRecaptchaV3() {
    const editProfile = 'editProfile';
    this.reCaptchaV3Service.execute(editProfile).pipe(takeUntil(this.unSubscribe));
  }

  private verifySSODomain(email: string) {
    this.companyService
      .isSSODomain(email)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        ({ isSSODomain }) => {
          this.isSSOUser = isSSODomain;
        },
        (error) => this.errorHandler.handle(error),
      );
  }
}
